import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CalendarPlus, Loader2, MapPin, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SubscriptionBanner from "@/components/attendance/SubscriptionBanner";
import EventForm from "@/components/attendance/EventForm";
import AnalyticsPanel from "@/components/attendance/AnalyticsPanel";
import OrgSettings from "@/components/attendance/OrgSettings";
import TeamPanel from "@/components/attendance/TeamPanel";
import { btnGold, errMsg, fmtDate, STATUS_STYLE } from "@/components/attendance/ui";

const TABS = [["events", "Events"], ["analytics", "Analytics"]];
const OWNER_TABS = [["team", "Team"], ["settings", "Settings"]];

export default function AttendanceDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [org, setOrg] = useState(null);
  const [plan, setPlan] = useState(null);
  const [meta, setMeta] = useState(null);
  const [events, setEvents] = useState(null);
  const [tab, setTab] = useState("events");
  const [creating, setCreating] = useState(false);

  const loadEvents = useCallback(() => api.get("/attendance/events").then((r) => setEvents(r.data)), []);
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/attendance/dashboard"); return; }
    (async () => {
      try {
        if (params.get("billing") === "success" && params.get("session_id")) {
          await api.post(`/attendance/billing/sync?session_id=${encodeURIComponent(params.get("session_id"))}`).catch(() => {});
          toast.success("Your free trial has started");
          setParams({});
        } else if (params.get("billing") === "cancelled") { toast("Checkout cancelled — you can start your trial anytime."); setParams({}); }
        const [o, p, m] = await Promise.all([api.get("/attendance/org"), api.get("/attendance/plans"), api.get("/attendance/meta")]);
        setOrg(o.data); setPlan(p.data); setMeta(m.data);
        if (o.data.my_roles?.includes("attendance")) loadEvents();
      } catch (e) {
        if (e?.response?.status === 404) nav("/attendance/register");
        else toast.error(errMsg(e, "Could not load your organisation"));
      }
    })();
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async (body) => {
    try { const r = await api.post("/attendance/events", body); setCreating(false); nav(`/attendance/events/${r.data.id}`); }
    catch (e) { toast.error(errMsg(e, "Could not create event")); }
  };

  if (!org || !meta) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  const canUse = org.my_roles?.includes("attendance");
  const isOwner = org.my_roles?.includes("owner");
  return (
    <div className="max-w-[1240px] mx-auto px-4 sm:px-6 lg:px-10 py-10" data-testid="attendance-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow mb-2">HOH Attendance</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-white" data-testid="att-org-name">{org.name}</h1>
        </div>
        {canUse && org.access.has_access && <button onClick={() => setCreating(true)} data-testid="att-new-event" className={btnGold}><CalendarPlus size={15} /> New event</button>}
      </div>
      {canUse && <SubscriptionBanner org={org} plan={plan} canBill={isOwner} />}
      {(canUse || isOwner) && <div className="flex gap-2 mt-8 mb-6 border-b border-white/10">
        {(canUse ? TABS : []).concat(isOwner ? OWNER_TABS : []).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`att-tab-${k}`}
            className={`px-4 py-3 text-xs tracking-widest uppercase -mb-px border-b-2 ${tab === k ? "border-[#D4AF37] text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>}
      {!canUse && (
        <div className="luxury-card rounded-xl p-6 text-sm text-neutral-300 mt-6" data-testid="att-not-authorised">
          You are not an Attendance administrator for {org.name}. {org.my_roles?.includes("projection") && <Link to="/projection/console" className="text-[#D4AF37] underline">Open Hymn Projection</Link>}
        </div>
      )}
      {canUse && tab === "events" && <EventList events={events} />}
      {canUse && tab === "analytics" && <AnalyticsPanel types={meta.event_types} />}
      {isOwner && tab === "team" && <TeamPanel />}
      {isOwner && tab === "settings" && <OrgSettings org={org} types={meta.org_types} onSaved={setOrg} />}
      {creating && <EventForm types={meta.event_types} defaults={meta.default_categories} onSave={create} onClose={() => setCreating(false)} />}
    </div>
  );
}

function EventList({ events }) {
  if (!events) return <Loader2 className="animate-spin text-[#D4AF37]" />;
  if (!events.length) return <p className="text-neutral-400 text-sm" data-testid="att-events-empty">No events yet. Create your first event, invite ushers, then open counting.</p>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="att-events-list">
      {events.map((e) => (
        <Link key={e.id} to={`/attendance/events/${e.id}`} data-testid={`att-event-${e.id}`} className="luxury-card rounded-xl p-5 hover:border-[#D4AF37]/50 transition-colors block">
          <div className="flex justify-between items-start gap-3">
            <span className="text-[10px] tracking-widest uppercase text-neutral-500">{e.custom_type || e.type}</span>
            <span className={`text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border ${STATUS_STYLE[e.status]}`}>{e.status === "open" ? "● live" : e.status}</span>
          </div>
          <div className="font-serif-display text-2xl text-white mt-2 leading-tight">{e.name}</div>
          <div className="text-xs text-neutral-400 mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <span>{fmtDate(e.date)} {e.start_time}</span>
            {e.venue && <span className="inline-flex items-center gap-1"><MapPin size={11} />{e.venue}</span>}
            <span className="inline-flex items-center gap-1"><Users size={11} />{e.usher_count} usher{e.usher_count === 1 ? "" : "s"}</span>
          </div>
          <div className="mt-4 font-serif-display text-4xl text-[#D4AF37]" data-testid={`att-event-total-${e.id}`}>{e.total ?? 0}</div>
        </Link>
      ))}
    </div>
  );
}
